import { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Datagrid, ListContextProvider, NumberField, TextField } from "react-admin";
import { settings } from "app";
import { SEMESTER_GRID_GAP, semesterGridColumns } from "./semesterGrid";

// Credits cover the whole education, not the course selected in the tabs above.
const CreditsSummary = ({ credits, isMobile }) => {
  const [expanded, setExpanded] = useState(true);

  const rows = [
    { id: "mandatory", type: "Задължителни", credits: credits?.mandatoryCredits },
    { id: "elective", type: "Избираеми", credits: credits?.electiveCredits },
    { id: "facultative", type: "Факултативни", credits: credits?.facultativeCredits },
    { id: "total", type: "Общо", credits: credits?.totalCredits },
  ];

  // An empty object response is truthy, so decide by the values — and keep a real 0 valid.
  const hasCredits = rows.some(row => row.credits != null);

  return (
    // Same grid as the disciplines tables above, so the header sits over its own column and not the page center.
    <Box
      sx={{
        display: "grid",
        // Same columns as the grades tables above, so the credits sit in the first column (semesterGrid.js).
        gridTemplateColumns: semesterGridColumns(isMobile),
        gap: SEMESTER_GRID_GAP,
        margin: (isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox).margin,
      }}
    >
      {/* Wrapped in a Box, not a fragment — the grid would otherwise treat the header and the table as separate cells.
          minWidth: 0 - a grid item otherwise stays as wide as its table and spills into the next column. */}
      <Box sx={{ minWidth: 0 }}>
        {/* shadowBox is sized for a full-width section — drop its width/margin, keep the framing. */}
        <Box
          sx={{
            ...(isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox),
            width: "auto",
            maxWidth: "none", // shadowBox's cap assumes its 2rem side margins, which are dropped here
            margin: 0,
            marginTop: "0.5rem",
            overflowX: "auto", // a table wider than the column scrolls here instead of widening the page
          }}
        >
          {/* Same title as „Избираеми“ / „Факултативни дисциплини“ (PickedDisciplines.js), plus the accordion toggle. */}
          <Box
            onClick={() => setExpanded(prev => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {/* Margins go in sx - an sx of its own is what keeps the theme's 14px Typography default off this title. */}
            <Typography
              color="info"
              fontWeight={600}
              sx={{ ml: 2 }}
            >
              Кредити
            </Typography>
            <ExpandMoreIcon
              color="primary"
              sx={{
                transition: "transform 0.2s",
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          </Box>

          <Collapse in={expanded}>
            {/* px matches the title's ml, so the table lines up with it instead of hugging the frame. */}
            <Box sx={{ pt: 2, px: 2 }}>
              {!hasCredits ? (
                <Typography
                  variant="body1"
                  color="secondary"
                >
                  Няма намерени данни
                </Typography>
              ) : (
                <ListContextProvider
                  value={{
                    data: rows,
                    ids: rows.map(row => row.id),
                    total: rows.length,
                    loaded: true,
                  }}
                >
                  <Datagrid
                    rowClick={false}
                    bulkActionButtons={false}
                    sx={settings.style.secondaryDatagrid}
                    rowSx={record => (record.id === "total" ? { "& .MuiTableCell-root": { fontWeight: 600 } } : null)}
                  >
                    <TextField
                      source="type"
                      label="Тип"
                      sortable={false}
                    />
                    <NumberField
                      source="credits"
                      label="Кредити"
                      sortable={false}
                      emptyText="-"
                    />
                  </Datagrid>
                </ListContextProvider>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};

export default CreditsSummary;
